import React, { useState, useEffect } from 'react';
import { FiPercent, FiSave, FiAlertTriangle, FiInfo, FiCheck } from 'react-icons/fi';
import Cookies from 'js-cookie';

/**
 * ReferralPercentage Component
 * Allows admin to configure the global referral percentage
 */
const ReferralPercentage = ({
  currentPercentage,
  currentSecondLevel,
  onSave,
  loading,
  lastUpdated,
  lastUpdatedBy
}) => {
  const [percentage, setPercentage] = useState(currentPercentage ?? 0);
  const [secondLevel, setSecondLevel] = useState(currentSecondLevel ?? 1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentPercentage !== undefined && currentPercentage !== null) {
      setPercentage(currentPercentage);
    }
  }, [currentPercentage]);

  useEffect(() => {
    if (currentSecondLevel !== undefined && currentSecondLevel !== null) {
      setSecondLevel(currentSecondLevel);
    }
  }, [currentSecondLevel]);

  const handleSecondLevelChange = (e) => {
    let value = parseFloat(e.target.value);
    if (isNaN(value)) value = 0;
    if (value < 0) value = 0;
    if (value > 100) value = 100;
    setSecondLevel(value);
    setSaveSuccess(false);
  };

  const handleSliderChange = (e) => {
    const value = parseFloat(e.target.value);
    setPercentage(value);
    setSaveSuccess(false);
  };

  const handleInputChange = (e) => {
    let value = parseFloat(e.target.value);
    if (isNaN(value)) value = 0;
    if (value < 0) value = 0;
    if (value > 100) value = 100;
    setPercentage(value);
    setSaveSuccess(false);
  };

  const handleSaveClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const adminId = Cookies.get("userId") || "admin";
      await onSave(percentage, secondLevel, adminId);
      setSaveSuccess(true);
      setShowConfirm(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save percentage');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanged =
    percentage !== currentPercentage || secondLevel !== currentSecondLevel;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-blue-500 text-white p-3 rounded-full">
          <FiPercent className="text-xl" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Referral Percentage</h3>
          <p className="text-sm text-gray-500">Configure the global referral reward percentage</p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <FiInfo className="text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How it works:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Both percentages apply to the event entry fee (and the onboarding bonus base)</li>
              <li><b>Level 1</b> (direct referrer) earns the Level&nbsp;1 percentage</li>
              <li><b>Level 2</b> (the referrer's referrer) earns the Level&nbsp;2 percentage</li>
              <li>e.g. L1 10% + L2 1% on a ₹1000 entry → ₹100 to L1 and ₹10 to L2</li>
              <li>Changes apply to future rewards only — existing rewards are unchanged</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Percentage Control */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">
            Level 1 — Direct referrer %
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={percentage}
              onChange={handleInputChange}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            />
            <span className="text-gray-500">%</span>
          </div>
        </div>
        
        <input
          type="range"
          min="0"
          max="100"
          step="0.1"
          value={percentage}
          onChange={handleSliderChange}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
          disabled={loading}
        />
        
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Level 2 percentage */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">
            Level 2 — Referrer's referrer %
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={secondLevel}
              onChange={handleSecondLevelChange}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            />
            <span className="text-gray-500">%</span>
          </div>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          step="0.1"
          value={secondLevel}
          onChange={handleSecondLevelChange}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
          disabled={loading}
        />
        <p className="text-xs text-gray-500 mt-1">
          Second-level (indirect) commission. Typically smaller than Level 1
          (e.g. 1%).
        </p>
      </div>

      {/* Current vs New */}
      {hasChanged && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-yellow-800">
            <FiAlertTriangle className="text-yellow-500" />
            <span className="text-sm">
              L1 <strong>{currentPercentage}%→{percentage}%</strong> · L2{" "}
              <strong>
                {currentSecondLevel}%→{secondLevel}%
              </strong>
            </span>
          </div>
        </div>
      )}

      {/* Last Updated Info */}
      {lastUpdated && (
        <div className="text-sm text-gray-500 mb-6">
          <span>Last updated: {formatDate(lastUpdated)}</span>
          {lastUpdatedBy && <span> by {lastUpdatedBy}</span>}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-red-800">
            <FiAlertTriangle className="text-red-500" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Success Message */}
      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-green-800">
            <FiCheck className="text-green-500" />
            <span className="text-sm">Percentage updated successfully!</span>
          </div>
        </div>
      )}

      {/* Save Button */}
      <button
        onClick={handleSaveClick}
        disabled={loading || isSaving || !hasChanged}
        className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
          hasChanged && !loading && !isSaving
            ? 'bg-blue-500 text-white hover:bg-blue-600'
            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
        }`}
      >
        <FiSave />
        {isSaving ? 'Saving...' : 'Save Changes'}
      </button>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-yellow-100 p-2 rounded-full">
                <FiAlertTriangle className="text-yellow-600 text-xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Confirm Change</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Apply these commission rates? — Level 1{" "}
              <strong>{currentPercentage}% → {percentage}%</strong>, Level 2{" "}
              <strong>{currentSecondLevel}% → {secondLevel}%</strong>.
            </p>
            
            <p className="text-sm text-gray-500 mb-6">
              This will affect all future referral rewards. Existing rewards will not be changed.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSave}
                disabled={isSaving}
                className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 disabled:bg-blue-300"
              >
                {isSaving ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralPercentage;
